// Admin / backend status dashboard. Gated to test users (profiles.is_test_user).
// Server functions enforce the same gate via requireAdminAuth — this UI guard
// is just for nicer rendering, not the security boundary.
import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { HealthPanel } from "@/components/admin/HealthPanel";
import { StoragePanel } from "@/components/admin/StoragePanel";
import { RlsPanel } from "@/components/admin/RlsPanel";
import { AdvisorsPanel } from "@/components/admin/AdvisorsPanel";

export const Route = createFileRoute("/admin/")({
  head: () => ({
    meta: [
      { title: "Admin · KnowMeno" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

type State =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | { status: "forbidden"; email: string | null }
  | { status: "ok"; email: string };

function AdminPage() {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      const session = sess.session;
      if (!session) {
        if (!cancelled) setState({ status: "unauthenticated" });
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_test_user, email")
        .eq("id", session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (profile?.is_test_user) {
        setState({ status: "ok", email: profile.email ?? session.user.email ?? "" });
      } else {
        setState({
          status: "forbidden",
          email: profile?.email ?? session.user.email ?? null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (state.status === "unauthenticated") {
    return (
      <CenteredMessage
        title="Sign in required"
        body={
          <>
            You need to be signed in to view this page.{" "}
            <Link to="/sign-in" className="underline">
              Sign in
            </Link>
            .
          </>
        }
      />
    );
  }

  if (state.status === "forbidden") {
    return (
      <CenteredMessage
        title="Not authorized"
        body={
          <>
            Your account ({state.email ?? "unknown"}) doesn't have admin access.
          </>
        }
      />
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Backend status</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only view of database health, storage, RLS policies, and lint
            findings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-600 hover:bg-emerald-600">
            Test user · admin access
          </Badge>
          <span className="text-sm text-muted-foreground">{state.email}</span>
        </div>
      </header>

      <Tabs defaultValue="health" className="w-full">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full">
          <TabsTrigger value="health">Health</TabsTrigger>
          <TabsTrigger value="storage">Storage</TabsTrigger>
          <TabsTrigger value="rls">RLS</TabsTrigger>
          <TabsTrigger value="advisors">Advisors</TabsTrigger>
        </TabsList>
        <TabsContent value="health" className="mt-4">
          <HealthPanel />
        </TabsContent>
        <TabsContent value="storage" className="mt-4">
          <StoragePanel />
        </TabsContent>
        <TabsContent value="rls" className="mt-4">
          <RlsPanel />
        </TabsContent>
        <TabsContent value="advisors" className="mt-4">
          <AdvisorsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CenteredMessage({
  title,
  body,
}: {
  title: string;
  body: React.ReactNode;
}) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-2">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}
